import { isExtensionSupported, isReferenceExtensionSupported } from '@bynder/image-gen-frontend-lib';
/**
 * Utility functions for image generation AI features
 */

import { AssetStatus } from 'packages/types/assets';

export type ImageGenInput = {
    status?: AssetStatus;
    mimeType?: string;
    extension?: string;
};

/**
 * List of allowed MIME types for AI image generation
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/tiff',
    'image/webp',
    'image/heif',
    'image/heic',
    'image/avif',
    'image/svg+xml',
    'application/postscript',
    'image/bmp',
    'image/vnd.wap.wbmp',
    'image/gif',
] as const;

/**
 * List of allowed MIME types for document reference assets (Smart Edit)
 */
export const ALLOWED_DOCUMENT_MIME_TYPES = ['application/pdf'] as const;

export const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx'] as const;

export const DOC_DOCX_EXTENSIONS = ['doc', 'docx'] as const;

/**
 * Check if the given MIME type is allowed for AI image generation
 * @param mimeType - MIME type string
 * @returns true if MIME type is allowed
 */
export const isAllowedMimeType = (mimeType: string): boolean => {
    return ALLOWED_IMAGE_MIME_TYPES.includes(mimeType.toLowerCase() as (typeof ALLOWED_IMAGE_MIME_TYPES)[number]);
};

export const isAllowedDocumentMimeType = (mimeType: string): boolean => {
    return ALLOWED_DOCUMENT_MIME_TYPES.includes(mimeType.toLowerCase() as (typeof ALLOWED_DOCUMENT_MIME_TYPES)[number]);
};

/**
 * Check if an image is allowed to be used for AI generation based on its source or MIME type
 * @param input - Object with src (URL), mime (MIME type), status, and mimeType
 * @returns true if the image format is allowed
 */
export const isAllowedImageFormat = (input: ImageGenInput): boolean => {
    if (!input) {
        return false;
    }

    if (input.mimeType) {
        return isAllowedMimeType(input.mimeType);
    }

    if (input.extension) {
        return isExtensionSupported(input.extension);
    }

    return false;
};

/**
 * Check if a file is allowed as a reference asset for Smart Edit (images + documents)
 */
export const isAllowedReferenceFormat = (input: ImageGenInput): boolean => {
    if (!input) {
        return false;
    }

    if (input.mimeType) {
        return isAllowedMimeType(input.mimeType) || isAllowedDocumentMimeType(input.mimeType);
    }

    if (input.extension) {
        return isReferenceExtensionSupported(input.extension);
    }

    return false;
};

// ----- UCV asset processing -----

export type UCVAssetMetadata = {
    selectedFile?: {
        thumbnail?: string;
        thumbnailUrl?: string;
        previewUrl?: string;
        url?: string;
        thumbnails?: Record<string, string>;
    };
};

export type ProcessedAsset = {
    databaseId: string;
    thumbnail: string | undefined;
    type: 'image' | 'document';
};

/**
 * Resolve the best available thumbnail URL from UCV asset data.
 * Follows the priority: files.thumbnail.url → selectedFile fields → asset fields.
 * For documents, originalUrl is skipped (it points to the file itself).
 */
export function resolveAssetThumbnail(
    asset: Record<string, any>,
    selectedFile: UCVAssetMetadata['selectedFile'],
    isDocument: boolean,
): string | undefined {
    const thumbnail =
        asset?.files?.thumbnail?.url ||
        selectedFile?.thumbnail ||
        selectedFile?.thumbnailUrl ||
        selectedFile?.previewUrl ||
        selectedFile?.thumbnails?.['320x320'] ||
        selectedFile?.url ||
        asset?.thumbnails?.['320x320'] ||
        asset?.thumbnails?.['1920x1920'] ||
        asset?.thumbnail ||
        asset?.thumbnailUrl ||
        asset?.previewUrls?.[0] ||
        asset?.previewUrl ||
        (!isDocument && asset?.originalUrl) ||
        '';

    return thumbnail || undefined;
}

/**
 * Process raw UCV assets into a payload for the GenAI modal.
 * Returns processed assets and a count of invalid ones (for error reporting).
 */
export function processPickerAssets(
    assets: any[],
    metadata: UCVAssetMetadata | undefined,
    options: { allowDocuments?: boolean },
): { payload: ProcessedAsset[]; invalidCount: number; docDocxCount: number } {
    const assetList = Array.isArray(assets) ? assets : [assets];
    const payload: ProcessedAsset[] = [];
    let invalidCount = 0;
    let docDocxCount = 0;

    for (const asset of assetList) {
        const rawId = asset?.databaseId ?? asset?.id;
        const databaseId = rawId ? String(rawId) : undefined;

        if (!databaseId) {
            continue;
        }

        const extension = asset?.extensions?.[0] || asset?.extension || '';
        const extensionLower = extension.toLowerCase();

        // Exclude DOC/DOCX files - not supported as reference assets
        if (DOC_DOCX_EXTENSIONS.includes(extensionLower as (typeof DOC_DOCX_EXTENSIONS)[number])) {
            docDocxCount++;
            continue;
        }

        const isValid = extension
            ? options.allowDocuments
                ? isReferenceExtensionSupported(extensionLower)
                : isExtensionSupported(extensionLower)
            : false;

        if (!isValid) {
            invalidCount++;
            continue;
        }

        const isDocument = DOCUMENT_EXTENSIONS.includes(extensionLower as (typeof DOCUMENT_EXTENSIONS)[number]);

        payload.push({
            databaseId,
            thumbnail: resolveAssetThumbnail(asset, metadata?.selectedFile, isDocument),
            type: isDocument ? 'document' : 'image',
        });
    }

    return { payload, invalidCount, docDocxCount };
}
